import { XUICompositionDetail } from '@xero/xui/react/compositions';
import XUILoader from '@xero/xui/react/loader';
import { XUIBreadcrumbTrail, XUIPageHeader } from '@xero/xui/react/pageheader';
import { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { setVisible } from '../../helpers/header';
import { deleteInstallationAction, installationSelectors } from '../../redux/slices/InstallationSlice';
import { getSettingsAction, putSettingsAction, settingsSelectors } from '../../redux/slices/SettingsSlice';
import { userSelectors } from '../../redux/slices/UserSlice';
import { organisationSelectors } from '../../redux/slices/OrganisationSlice';
import { useAppDispatch } from '../../redux/store';
import { beanieService } from '../../services/beanie/shopify';
import { integrationTypes } from '../../static/constants';
import lang from '../../static/lang/en-NZ';
import HubspotSetupPanel from './Hubspot';
import './Setup.css';
import { ShopifySetupPanel } from './Shopify';
import { beginFreeTrialAction } from '../../redux/slices/BeginFreeTrialSlice';
import { bootstrapSelectors, getBootstrapAction } from '../../redux/slices/BootstrapSlice';
import { FetchError, IInstallationSettings } from '../../models';
import ErrorComponent from '../../components/ErrorComponent/ErrorComponent';
import Link from '../../components/Link';
import React from 'react';

export interface ISetupProps {
    onSaveClick: (isInitialSetup: boolean) => void;
    onCancelClick: () => void;
    onError: (error: any) => void;
}

const setupPanels = new Map();
setupPanels.set(integrationTypes.HUBSPOT, HubspotSetupPanel);
setupPanels.set(integrationTypes.SHOPIFY, ShopifySetupPanel);

const Setup: FC<ISetupProps> = ({ onSaveClick, onCancelClick, onError }) => {
    const dispatch = useAppDispatch();
    const installation = useSelector(installationSelectors.installation);
    const user = useSelector(userSelectors.user);
    const organisation = useSelector(organisationSelectors.organisation);
    const isSyncingSettings = useSelector(settingsSelectors.isSyncing);

    const [isSaving, setIsSaving] = useState(false);

    const settingsError = useSelector(settingsSelectors.error);
    const bootstrapError = useSelector(bootstrapSelectors.error);
    const installationError = useSelector(installationSelectors.error);

    const [backendError, setBackendError] = useState<Error | undefined>();

    useEffect(() => {
        // no connection call in set up flow
        if (settingsError) {
            setBackendError(settingsError);
        }
        if (bootstrapError) {
            setBackendError(bootstrapError);
        }
        if (installationError) {
            setBackendError(installationError);
        }
    }, [settingsError, bootstrapError, installationError]);

    useEffect(() => {
        document.body.style.background = '#f2f3f4';
        setVisible();
        dispatch(getSettingsAction());
    }, [dispatch]);

    const handleCancelClick = () => {
        setIsSaving(true);
        onCancelClick();
    };

    const handleSaveClick = async (
        settings: IInstallationSettings,
        isFinal?: boolean,
        startTrialOnFinalSave?: boolean
    ) => {
        try {
            await dispatch(
                putSettingsAction({
                    ...settings,
                    isFinal,
                })
            );

            if (isFinal) {
                if (!installation.isOnboarded) {
                    await beanieService.sendOutboundConnectionCompletedEvent(
                        installation.integrationType,
                        organisation.id,
                        user
                    );
                    if (
                        startTrialOnFinalSave &&
                        installation.freeTrial?.isEligibleForTrial &&
                        !installation.billing?.hasBought
                    ) {
                        await dispatch(beginFreeTrialAction());
                    }
                }

                await dispatch(getBootstrapAction());
                onSaveClick(!installation.isOnboarded);
            }
        } catch (error) {
            onError(error);
        }
    };

    const SetupPanel = setupPanels.get(installation.integrationType);
    if (!SetupPanel) {
        return <XUILoader ariaLabel='Loading' />;
    }

    const breadcrumbLinks = [
        { key: 'a', label: lang.HEADINGS.APPS, href: lang.URLS.APPS },
        <span key='b' tabIndex={0} role='link' onClick={handleCancelClick}>
            {installation.displayName}
        </span>,
    ];

    const onGoBack = useCallback(() => {
        if (!installation.isOnboarded) {
            dispatch(deleteInstallationAction(false)).then(() => window.location.reload());
        }
    }, [dispatch, installation.isOnboarded]);

    const goBackButton = React.useMemo(
        () => ({
            onClick: onGoBack,
            text: lang.ERROR.GO_BACK,
        }),
        [onGoBack]
    );

    const setup = (
        <>
            <XUIPageHeader
                breadcrumb={<XUIBreadcrumbTrail breadcrumbs={breadcrumbLinks} />}
                title={`${installation.displayName} ${lang.HEADINGS.SETTINGS}`}
            />
            <XUICompositionDetail
                className='setup--composition'
                detail={
                    <div data-automationid='settings-page' className='setup--container'>
                        <div className='setup--panel xui-padding-top-3xlarge xui-u-flex xui-u-flex-column'>
                            <SetupPanel
                                isSaving={isSaving || isSyncingSettings}
                                onSaveClick={handleSaveClick}
                                onCancelClick={handleCancelClick}
                            />
                        </div>
                    </div>
                }
            />
        </>
    );

    if (backendError) {
        const error = backendError as FetchError;
        if (error.code && error.code >= 500 && error.code < 600) {
            return (
                <ErrorComponent
                    description={
                        <div>
                            {lang.ERROR.GO_BACK_WAIT}
                            <Link displayLinkIcon={true} href={lang.ERROR.FIND_AN_ANSWER_LINK}>
                                {lang.ERROR.FIND_AN_ANSWER}
                            </Link>
                        </div>
                    }
                    heading={lang.ERROR.SOMETHING_WENT_WRONG}
                    primaryButton={goBackButton}
                />
            );
        }
        return setup;
    }
    return setup;
};

export default Setup;
